import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Truck, Shield, RefreshCw, Headphones, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ProductCarousel } from '../components/ProductCarousel';
import { HomeHero } from '../components/home/HomeHero';
import { useSettings } from '../context/SettingsContext';
import SEO from '../components/SEO';
import { PLACEHOLDER_IMAGE } from '../lib/placeholder';
import { PlacementBanner, BannerSlot, usePlacementBanner } from '../components/CategoryHeroBanner';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND_URL}/api`;

const formatPrice = (value) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })
    .format(Number(value) || 0);

export default function HomePage() {
  const { freeThreshold } = useSettings();
  // Fills the offer block's image well. Read as a hook so the fallback art is
  // never painted and then swapped.
  const offerBanner = usePlacementBanner('hero_secondary');
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [bestSellers, setBestSellers] = useState([]);
  const [trendingProducts, setTrendingProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [featuredRes, bestRes, trendingRes, catRes] = await Promise.all([
          fetch(`${API}/products?featured=true&limit=12`),
          fetch(`${API}/products?bestseller=true&limit=12`),
          fetch(`${API}/products?trending=true&limit=12`),
          fetch(`${API}/categories`)
        ]);

        const [featured, best, trending, cats] = await Promise.all([
          featuredRes.json(),
          bestRes.json(),
          trendingRes.json(),
          catRes.json()
        ]);

        setFeaturedProducts(featured.products || []);
        setBestSellers(best.products || []);
        setTrendingProducts(trending.products || []);
        setCategories(cats || []);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const categoryImages = {
    'cat_optical': 'https://images.unsplash.com/photo-1574258495973-f010dfbb5371?w=800',
    'cat_mobile': 'https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?w=800',
    'cat_giftcard': 'https://images.unsplash.com/photo-1549465220-1a8b9238cd48?w=800'
  };

  return (
    <div className="min-h-screen bg-white" data-testid="home-page">
      <SEO 
        title="NSO India | Premium E-commerce Destination" 
        description="NSO India is your premium digital marketplace for high-quality optical eyewear, the latest smartphones, and versatile digital gift cards. Shop now for the best deals!"
      />
      {/*
        Hero. Serves whatever is published to `hero_main` in Banner Management,
        and falls back to the built-in category slider when nothing is — so the
        homepage never depends on the banner service being populated.
      */}
      <HomeHero />


      {/* Trust strip — compact bar, not a full section */}
      <section className="border-y border-gray-100 bg-white" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-gray-100">
            {[
              { icon: Truck, title: 'Free Shipping', desc: `Above ${formatPrice(freeThreshold)}` },
              { icon: Shield, title: 'Secure Payment', desc: 'UPI · Cards · COD' },
              { icon: RefreshCw, title: 'Easy Returns', desc: '7-day window' },
              { icon: Headphones, title: 'Real Support', desc: 'Mon–Sat, 10–7' }
            ].map((feature, idx) => (
              <div key={idx} className="flex items-center gap-3 py-4 px-3 md:px-5">
                <feature.icon className="w-5 h-5 text-primary flex-shrink-0" />
                <div className="min-w-0">
                  <p className="font-body font-semibold text-secondary text-[13px] leading-tight truncate">{feature.title}</p>
                  <p className="text-[11px] text-muted-foreground leading-tight truncate">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Promo strip from Admin → Banner Management (placement: home_top).
          Renders nothing at all until someone publishes one. */}
      <PlacementBanner placement="home_top" />

      {/* Categories */}
      <section className="py-8 md:py-10" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-6">
            <span className="text-[11px] uppercase tracking-widest text-primary font-body font-semibold mb-1.5 block">
              Shop by Category
            </span>
            <h2 className="font-heading text-2xl md:text-[32px] tracking-tight text-secondary">
              Browse Collections
            </h2>
          </div>

          {/* Four across on desktop: there are four categories, and a 3-column grid
              stranded the fourth on its own row next to a two-thirds-wide gap. */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
            {categories.map((cat, idx) => (
              <Link
                key={cat.category_id}
                to={`/category/${cat.slug}`}
                className="group relative aspect-[5/4] sm:aspect-[4/3] rounded-2xl overflow-hidden ring-1 ring-slate-900/[0.04] transition-shadow duration-200 hover:shadow-[0_10px_30px_rgba(16,24,40,0.12)]"
                data-testid={`category-link-${cat.slug}`}
              >
                <img
                  src={categoryImages[cat.category_id] || cat.image || PLACEHOLDER_IMAGE}
                  alt={cat.name}
                  onError={(e) => { if (e.currentTarget.src !== PLACEHOLDER_IMAGE) e.currentTarget.src = PLACEHOLDER_IMAGE; }}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />
                <div className="absolute bottom-3 left-3 right-3">
                  <h3 className="font-heading text-[15px] md:text-lg text-white mb-0.5 leading-tight">{cat.name}</h3>
                  <p className="text-white/75 font-body text-[11px] mb-2 line-clamp-1">{cat.description}</p>
                  <span className="inline-flex items-center text-white font-body text-xs font-semibold group-hover:gap-2.5 gap-1.5 transition-all">
                    Explore <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <div className="bg-surface" data-testid="featured-section">
        <ProductCarousel
          eyebrow="Curated Selection"
          title="Featured Products"
          viewAllHref="/products?featured=true"
          products={featuredProducts}
          loading={loading}
        />
      </div>

      <div data-testid="bestsellers-section">
        <ProductCarousel
          eyebrow="Most Popular"
          title="Best Sellers"
          viewAllHref="/products?bestseller=true"
          products={bestSellers}
          loading={loading}
        />
      </div>

      {/* Special Offer Banner */}
      <section className="py-8 md:py-10 bg-gradient-to-br from-brand-blue-dark to-brand-blue" data-testid="offer-section">
        {/* Wider container and an uneven split, both for the artwork's benefit:
            a 2.75:1 banner in a half-width column is only ~210px tall, which
            reads as an afterthought next to a 330px block of copy. Width is the
            only lever on a fixed-ratio image — height follows it. */}
        <div className="max-w-[1600px] mx-auto px-4 md:px-8">
          <div className="grid items-center gap-8 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] lg:gap-10">
            <div>
              {/* Accent colours here must NOT be `text-primary`: this section's background
                  is now brand blue, so blue text on it is invisible. It only worked while
                  the background was black. Yellow and orange are the logo colours that
                  actually read against blue. */}
              <span className="inline-block text-sm uppercase tracking-widest text-brand-yellow font-body font-bold mb-4">
                Limited Time Offer
              </span>
              <h2 className="font-heading text-3xl md:text-5xl tracking-tight text-white mb-5">
                Free delivery on<br />orders above {formatPrice(freeThreshold)}
              </h2>
              <p className="text-base text-white/75 font-body mb-7 max-w-md leading-relaxed">
                No coupon needed — it applies automatically at checkout. Plus 7-day returns
                on everything, with refunds straight to your NSO wallet.
              </p>
              <Button
                asChild
                className="bg-brand-orange hover:bg-brand-orange-dark text-secondary rounded-full px-8 py-6 text-lg font-body font-semibold btn-press"
                data-testid="claim-offer-btn"
              >
                <Link to="/products">
                  Start Shopping <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
            </div>
            {/*
              The image well is served by Banner Management (Homepage Secondary)
              and falls back to the stock shot when nothing is published. The
              slot takes the banner's own ratio rather than forcing 16:9, so a
              2.75:1 banner is not cut down to fit.
            */}
            <div className="relative">
              {offerBanner ? (
                <BannerSlot banner={offerBanner} />
              ) : (
                <div className="aspect-video rounded-3xl overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1695048133142-1a20484d2569?w=800"
                    alt="Special Offer"
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* White, not surface: the "Why NSO India" block below is already surface, and two
          adjacent grey sections merge into one undifferentiated slab. */}
      <PlacementBanner placement="home_middle" variant="contained" />

      <div data-testid="trending-section">
        <ProductCarousel
          eyebrow="What's Hot"
          title="Trending Now"
          viewAllHref="/products?trending=true"
          products={trendingProducts}
          loading={loading}
        />
      </div>

      {/*
        This slot held three hardcoded testimonials with invented names, ratings and
        products. The store has no reviews yet, so they were not placeholders awaiting
        data — they were fabricated social proof on a live commercial page, which the
        Consumer Protection Act 2019 and ASCI guidelines treat as misleading.

        Replaced with claims that are actually verifiable about this store. When real
        reviews exist, GET /api/reviews/:productId can feed a genuine testimonial rail
        here — sourced, not written.
      */}
      {/* Homepage Bottom — last chance before the page turns to reassurance copy. */}
      <PlacementBanner placement="home_bottom" variant="contained" />
      <PlacementBanner placement="hero_bottom" variant="contained" />

      <section className="py-8 md:py-10 bg-surface" data-testid="why-us-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-6">
            <span className="text-[11px] uppercase tracking-widest text-primary font-body font-semibold mb-1.5 block">
              Why NSO India
            </span>
            <h2 className="font-heading text-2xl md:text-[32px] tracking-tight text-secondary">
              Bought right, delivered right
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              {
                icon: Shield,
                title: 'Authorised stock only',
                body: 'Every product is sourced through official channels and carries full brand warranty.'
              },
              {
                icon: FileText,
                title: 'Proper GST invoice',
                body: 'A compliant tax invoice with every order — download it from your account any time.'
              },
              {
                icon: RefreshCw,
                title: '7-day returns',
                body: 'Changed your mind? Raise a return from My Orders and the refund lands in your wallet.'
              },
              {
                icon: Truck,
                title: 'Tracked delivery',
                body: 'Live courier tracking from dispatch to doorstep, with delivery estimates by pincode.'
              }
            ].map((item, idx) => (
              <div key={idx} className="bg-white rounded-3xl p-6 shadow-soft">
                <div className="w-11 h-11 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
                  <item.icon className="w-5 h-5 text-primary" />
                </div>
                <h3 className="font-body font-semibold text-secondary mb-1.5">{item.title}</h3>
                <p className="text-sm text-muted-foreground font-body leading-relaxed">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
